import { useContext, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { Link } from 'react-router'
import { ContentCopyOutlined, Edit } from '@mui/icons-material'
import { Box, Button, CircularProgress, DialogActions, DialogContent, Grid, IconButton, Tooltip, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import AddressForm from '../../../../components/address-form'
import DialogWithTitle from '../../../../components/dialog-with-title'
import FormikTextField from '../../../../components/formik-textfield'
import Section from '../../../../components/section'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import { GLOBAL_API } from '../../../../utils/constants'
import CountriesStatesList from '../../../../utils/countries-and-states'
import { formatAddress, getFormattedDateTime, prettifyPhoneNumber } from '../../../../utils/helpers'
import useClipboard from '../../../../utils/hooks/useClipboard'
import useSharedStyles from '../../../../utils/shared.styles'
import AccountsContext from '../../context'

import DetailsTimeline from './details-timeline'
import useStyles from './index.styles'

const addressSchema = Yup.object().shape({
  company: Yup.string().required('Required'),
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  phone: Yup.string(),
  address: Yup.string().required('Required'),
  locality: Yup.string().required('Required'),
  region: Yup.string().required('Required'),
  code: Yup.string().required('Required'),
  country: Yup.string().required('Required'),
})

interface ExtendedFormValues {
  company: string
  firstName: string
  lastName: string
  phone: string
}

const Details = ({ aboutData, refetch }: { aboutData: IFolderAbout; refetch: () => Promise<void> }) => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const {
    enrolled,
    participantAgreementDetails,
    participantAgreementText,
    accountStatusInfo,
    defaultShippingAddress,
    installOption,
    lastInstallerId,
    lastInstallerName,
    lastUpdated,
  } = aboutData
  const { folder, setFolderRefreshCounter, folderAttributesLoading } = useContext(AccountsContext)
  const auth = useAuth()
  const host = useHost()
  const { tenantId } = useContext(AppContext)
  const { enqueueSnackbar } = useSnackbar()

  const { clickToCopy } = useClipboard()

  const [editAddress, setEditAddress] = useState(false)

  const countryDetails = (address: AddressFormValues) => CountriesStatesList.find((c) => c.name === address.country || c.abbreviation === address.country)
  const stateDetails = useMemo(
    () => (address: AddressFormValues) => countryDetails(address)?.states.find((s) => s.name === address.region || s.abbreviation === address.region),
    []
  )

  const initialAddressValues: ExtendedFormValues & AddressFormValues = useMemo(
    () => ({
      ...defaultShippingAddress,
      phone: prettifyPhoneNumber(defaultShippingAddress.phone),
      region: stateDetails(defaultShippingAddress)?.abbreviation ?? defaultShippingAddress.region, // Endpoint sends region back as full state name (e.g. Texas) instead of abbreviation (e.g. TX); the form is designed for the abbreviation
    }),
    [defaultShippingAddress, stateDetails]
  )

  const onSubmitAddress = async (values: ExtendedFormValues & AddressFormValues) => {
    if (!host || !auth.user?.access_token) return

    if (folder) {
      const validateAddressRes = await Api.get(
        `${GLOBAL_API}/api/v1/validate/address?address=${values.address}&address2=${values.address2}&region=${values.region}&country=${values.country}&code=${values.code}&locality=${values.locality}`
      )

      if (validateAddressRes.ok && validateAddressRes.body.isValid) {
        const res = await Api.patch(`${host}/api/v2/folders/${folder.id}/attributes`, {
          defaultShippingAddress: {
            address1: values.address,
            address2: values.address2,
            city: values.locality,
            provinceCode: stateDetails(values)?.abbreviation ?? values.region,
            province: stateDetails(values)?.name ?? values.region,
            country: countryDetails(values)?.name ?? values.country,
            countryCode: countryDetails(values)?.abbreviation ?? values.country,
            zip: values.code,
            company: values.company,
            firstName: values.firstName,
            lastName: values.lastName,
            name: `${values.firstName} ${values.lastName}`,
            phone: values.phone,
          },
        })
        if (res.ok) {
          enqueueSnackbar('Address updated successfully')
          setEditAddress(false)
          setFolderRefreshCounter((c) => c + 1)
          refetch()
        } else {
          enqueueSnackbar('Could not update default shipping address', { variant: 'error' })
        }
      } else if (validateAddressRes.body.isValid === false) {
        enqueueSnackbar('Address is not valid', { variant: 'error' })
      } else if (!validateAddressRes.ok) {
        enqueueSnackbar('Could not validate address', { variant: 'error' })
      }
    }
  }

  const timelines = [
    {
      dotSuccess: !!enrolled?.updatedAt,
      title: 'Account Enrolled',
      updatedAt: enrolled?.updatedAt,
      updatedBy: enrolled?.updatedBy,
    },
    {
      dotSuccess: !!participantAgreementDetails?.updatedAt,
      title: 'Participant Agreement',
      updatedAt: participantAgreementDetails?.updatedAt,
      updatedBy: participantAgreementDetails?.updatedBy,
      isPartipantAgreement: true,
    },
    {
      dotSuccess: !!accountStatusInfo?.updatedAt,
      title: 'Account Activated',
      updatedAt: accountStatusInfo?.updatedAt,
      updatedBy: accountStatusInfo?.updatedBy,
    },
  ]

  return (
    <Section
      title="Details"
      component={
        <Grid
          container={true}
          sx={{
            marginBottom: 2,
          }}
        >
          <Grid size={{ xs: 12, lg: 4 }}>
            <DetailsTimeline timelines={timelines} participantAgreementText={participantAgreementText} />
          </Grid>

          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={classes.addressContainer}>
              <Grid
                container={true}
                sx={{
                  gap: 1,
                }}
              >
                <div>
                  <Grid
                    container={true}
                    sx={{
                      gap: 2,
                    }}
                  >
                    <Typography variant="overline" sx={sharedClasses.secondaryText}>
                      Default Shipping Address
                    </Typography>

                    <div>
                      {initialAddressValues && formatAddress(initialAddressValues, initialAddressValues.country) && (
                        <Tooltip title="Copy" disableInteractive={true}>
                          <IconButton
                            size="small"
                            onClick={() => clickToCopy(formatAddress(initialAddressValues, initialAddressValues.country))}
                            aria-label="Copy address to clipboard"
                          >
                            <ContentCopyOutlined fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <IconButton onClick={() => setEditAddress(true)} aria-label="Edit shipping address">
                        <Edit fontSize="small" color="primary" />
                      </IconButton>

                      <DialogWithTitle title="Shipping Address" open={editAddress} setOpen={setEditAddress}>
                        <Formik initialValues={initialAddressValues} onSubmit={onSubmitAddress} validationSchema={addressSchema} enableReinitialize={true}>
                          {(formikForm) => {
                            const { isSubmitting, dirty, isValid } = formikForm

                            return (
                              <Form>
                                <DialogContent>
                                  <Typography variant="subtitle2">Ship to</Typography>

                                  <Grid
                                    container={true}
                                    columnSpacing={1}
                                    size={12}
                                    sx={{
                                      marginBottom: 2,
                                    }}
                                  >
                                    <Grid size={{ xs: 12, lg: 6 }}>
                                      <FormikTextField label="Organization" name="company" />
                                    </Grid>

                                    <Grid size={{ xs: 12, lg: 6 }}>{/* nothing, taking up blank space */}</Grid>

                                    <Grid size={{ xs: 12, lg: 6 }}>
                                      <FormikTextField label="First Name" name="firstName" className="fs-exclude" />
                                    </Grid>

                                    <Grid size={{ xs: 12, lg: 6 }}>
                                      <FormikTextField label="Last Name" name="lastName" className="fs-exclude" />
                                    </Grid>

                                    <Grid size={{ xs: 12, lg: 6 }}>
                                      <FormikTextField label="Phone" name="phone" className="fs-exclude" />
                                    </Grid>
                                  </Grid>

                                  <Typography variant="subtitle2">Address</Typography>

                                  <AddressForm fullWidth={true} />

                                  <DialogActions>
                                    <Button onClick={() => setEditAddress(false)}>Cancel</Button>

                                    <Button variant="contained" type="submit" disabled={isSubmitting || !dirty || !isValid}>
                                      Save
                                    </Button>
                                  </DialogActions>
                                </DialogContent>
                              </Form>
                            )
                          }}
                        </Formik>
                      </DialogWithTitle>
                    </div>
                  </Grid>

                  {folderAttributesLoading && (
                    <Grid container={true} sx={sharedClasses.centerContentHorizontal}>
                      <CircularProgress />
                    </Grid>
                  )}

                  {!folderAttributesLoading && (
                    <>
                      {(initialAddressValues.firstName || initialAddressValues.lastName) && (
                        <Typography variant="body1">
                          {initialAddressValues.firstName} {initialAddressValues.lastName}
                        </Typography>
                      )}

                      {/* <Typography variant="body1">willfaith@aol.com</Typography> */}
                      <Typography variant="body1">{initialAddressValues.phone}</Typography>
                      <Box sx={classes.spacer} />
                      <Typography variant="body1">{initialAddressValues.company}</Typography>
                      <Typography variant="body1">{initialAddressValues.address}</Typography>
                      <Typography variant="body1">
                        {formatAddress(
                          `${initialAddressValues.locality}, ${initialAddressValues.region} ${initialAddressValues.code} ${initialAddressValues.country}`,
                          initialAddressValues.country
                        )}
                      </Typography>
                    </>
                  )}
                </div>
              </Grid>
            </Box>
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 4,
            }}
          >
            <Grid
              container={true}
              sx={[
                { flexDirection: 'column' },
                {
                  gap: 2,
                },
                classes.installerContainer,
              ]}
            >
              {installOption && (
                <div>
                  <Typography variant="overline" sx={sharedClasses.secondaryText}>
                    Install Option
                  </Typography>
                  <Typography variant="body1">{installOption}</Typography>
                </div>
              )}

              {lastInstallerId ? (
                <div>
                  <Typography variant="overline" sx={sharedClasses.secondaryText}>
                    Installer
                  </Typography>
                  <div>
                    <Link to={`/tenant/${tenantId}/people/${lastInstallerId}`}>{lastInstallerName || lastInstallerId}</Link>
                  </div>
                </div>
              ) : null}

              <div>
                <Typography variant="overline" sx={sharedClasses.secondaryText}>
                  Last Updated & By
                </Typography>
                <Typography variant="body1">
                  {lastUpdated?.updatedAt
                    ? `${getFormattedDateTime(lastUpdated.updatedAt)}${lastUpdated?.updatedBy ? ` by ${lastUpdated?.updatedBy}` : ''}`
                    : 'N/A'}
                </Typography>
              </div>
            </Grid>
          </Grid>
        </Grid>
      }
      showEdit={false}
    />
  )
}

export default Details
